import i18n from "i18n";
import { action, observable } from "mobx";

import type { ContentAdapterAPI } from "generated/types";

import {
  getEdifyModuleDetails,
  getEdifySubjectDetails,
  getEdifyTopicDetails,
} from "repositories/DiscoContentRepository";

import { ToastMethods } from "components/ToastNotification";

interface DiscoContentStoreInterface {
  loading: boolean;
  setLoading: (isLoading: boolean) => void;
  edifyModuleDetails?: ContentAdapterAPI.EdifyModuleDetails | null;
  fetchEdifyModuleDetails: (packageId: string) => void;
  setEdifyModuleDetails: (moduleDetails: ContentAdapterAPI.EdifyModuleDetails | null) => void;
  edifySubjectDetails?: ContentAdapterAPI.EdifySubjectDetails | null;
  fetchEdifySubjectDetails: (packageId: string, themeId: string) => void;
  setEdifySubjectDetails: (subjectDetails: ContentAdapterAPI.EdifySubjectDetails | null) => void;
  edifyTopicDetails?: ContentAdapterAPI.EdifyTopicDetails | null;
  fetchEdifyTopicDetails: (packageId: string, themeId: string, contentUnitId: string) => void;
  setEdifyTopicDetails: (topicDetails: ContentAdapterAPI.EdifyTopicDetails | null) => void;
}

const initialState = {
  loading: false,
  edifyModuleDetails: undefined,
  edifySubjectDetails: undefined,
  edifyTopicDetails: undefined,
};

const stateSetters = {
  setLoading: action((loading: boolean) => {
    store.loading = loading;
  }),
  setEdifyModuleDetails: action((moduleDetails: ContentAdapterAPI.EdifyModuleDetails | null) => {
    store.edifyModuleDetails = moduleDetails;
  }),
  setEdifySubjectDetails: action((subjectDetails: ContentAdapterAPI.EdifySubjectDetails | null) => {
    store.edifySubjectDetails = subjectDetails;
  }),
  setEdifyTopicDetails: action((topicDetails: ContentAdapterAPI.EdifyTopicDetails | null) => {
    store.edifyTopicDetails = topicDetails;
  }),
};

const apiRequests = {
  fetchEdifyModuleDetails: action((packageId: string) => {
    store.setLoading(true);
    getEdifyModuleDetails(packageId)
      .then((moduleDetails: ContentAdapterAPI.EdifyModuleDetails) => {
        store.setEdifyModuleDetails(moduleDetails);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:discoContent.getEdifyModuleDetails.error"), "error");
        store.setEdifyModuleDetails(null);
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  fetchEdifySubjectDetails: action((packageId: string, themeId: string) => {
    store.setLoading(true);
    getEdifySubjectDetails(packageId, themeId)
      .then((subjectDetails: ContentAdapterAPI.EdifySubjectDetails) => {
        store.setEdifySubjectDetails(subjectDetails);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:discoContent.getEdifySubjectDetails.error"), "error");
        store.setEdifySubjectDetails(null);
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  fetchEdifyTopicDetails: action((packageId: string, themeId: string, contentUnitId: string) => {
    store.setLoading(true);
    getEdifyTopicDetails(packageId, themeId, contentUnitId)
      .then((topicDetails: ContentAdapterAPI.EdifyTopicDetails) => {
        store.setEdifyTopicDetails(topicDetails);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:discoContent.getEdifyTopicDetails.error"), "error");
        store.setEdifyTopicDetails(null);
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
};

const store = observable({
  ...initialState,
  ...stateSetters,
  ...apiRequests,
} as DiscoContentStoreInterface);

export const useDiscoContent = (): DiscoContentStoreInterface => store;
